import { useState, useMemo } from 'react';
import {
  User, Loader2, Edit, Shield, X, CheckCircle, AlertCircle,
  Lock, Key, Copy, RefreshCw, Eye, EyeOff, Send, Trash2, UserCheck,
  Mail, Phone, Briefcase,
} from 'lucide-react';
import { DetailHeader } from '@/Components/shared/DetailHeader';
import { InfoCard, InfoRow } from '@/Components/shared/InfoCard';
import { useExternalUser, useUpdateExternalUser, useUpdateUserPermissions, useSetPasswordForExternalUser, useResendWelcomeEmail, useDeleteExternalUser } from '../hooks/useExternalUsers';
import { useChangePassword } from '@/hooks/auth/useChangePassword';
import { SocietiesAssignmentCard } from '../components/SocietiesAssignmentCard';
import { GenericAuditLogsCard } from '@/Components/shared/GenericAuditLogsCard';
import { getAvailablePermissionsWithWording, getPermissionsForUserType } from '../constants/permissions';
import { PermissionsSelector } from '@/Components/shared/PermissionsSelector';
import { useAuthStore } from '@/Stores/auth-v2-store';
import { PhoneInput } from '@/Components/shared/PhoneInput';
import { useSocietyChildren, useSociety } from '@/features/societies/hooks/useSocieties';
import { useEffectiveConfiguration } from '@/features/societies/hooks/useSocietyConfiguration';
import { useIsWarrantyPolicyOwner } from '@/features/warranties/hooks/useIsWarrantyPolicyOwner';
import { useCurrentUserWording } from '@/hooks/useVendorWording';

interface Notify {
  success: (msg: string) => void;
  error: (msg: string) => void;
}

interface FieldChange {
  field: string;
  label: string;
  oldValue: string;
  newValue: string;
}

const FIELD_LABELS: Record<string, string> = {
  civility: 'Civilité',
  firstname: 'Prénom',
  lastname: 'Nom',
  email: 'Email',
  phone: 'Téléphone',
  position: 'Poste',
};

export interface ExternalUserDetailPagePermissions {
  canEditPersonalInfo: boolean;
  canEditPermissions: boolean;
  canEditPassword: boolean;
  canEditSocieties: boolean;
  canResendWelcomeEmail: boolean;
  canImpersonate: boolean;
  canDelete: boolean;
  canViewAuditLogs: boolean;
}

export interface ExternalUserDetailPageProps {
  userId: string;
  notify: Notify;
  onNavigate: (path: string) => void;
  basePath?: string;
  permissions: ExternalUserDetailPagePermissions;
  confirmAction: (opts: { title: string; message: string; confirmLabel: string; variant: 'danger' | 'warning' | 'info' }) => Promise<boolean>;
  onImpersonate?: (userId: string) => Promise<void>;
  onLogout?: () => void;
  className?: string;
  /** Mode embarqué : le Layout parent gère le padding et le fond */
  embedded?: boolean;
}

export const ExternalUserDetailPage: React.FC<ExternalUserDetailPageProps> = ({
  userId,
  notify,
  onNavigate,
  basePath = '/users-externes',
  permissions: perms,
  confirmAction,
  onImpersonate,
  onLogout,
  className,
  embedded,
}) => {
  const { currentUser } = useAuthStore();
  const wording = useCurrentUserWording();
  const { data: user, isLoading, error } = useExternalUser(userId);
  const updateUser = useUpdateExternalUser();
  const updatePermissions = useUpdateUserPermissions();
  const changePassword = useChangePassword();
  const setPassword = useSetPasswordForExternalUser();
  const resendWelcomeEmail = useResendWelcomeEmail();
  const deleteUser = useDeleteExternalUser();

  const { data: primarySociety } = useSociety(user?.primarySocietyId);
  const { data: effectiveConfig } = useEffectiveConfiguration(user?.primarySocietyId || undefined);
  const { data: childrenData } = useSocietyChildren(user?.primarySocietyId || '');
  const hasChildren = useMemo(() => (childrenData || []).length > 0, [childrenData]);
  const isWarrantyPolicyOwner = useIsWarrantyPolicyOwner(user?.primarySocietyId);

  const societyId = user?.primarySocietyId;
  const societyOptions = useMemo(() => effectiveConfig ? {
    deliveryProofCreate: effectiveConfig.deliveryProofCreate,
    defineProvision: effectiveConfig.defineProvision,
    guarantorId: effectiveConfig.guarantorId,
    isTransferPrice: effectiveConfig.isTransferPrice,
    isWarrantyPolicyOwner,
    billedId: effectiveConfig.billedId,
    lockChildrenPriceEditing: effectiveConfig.lockChildrenPriceEditing,
    ownSocietyId: societyId,
  } : undefined, [effectiveConfig?.deliveryProofCreate, effectiveConfig?.defineProvision, effectiveConfig?.guarantorId, effectiveConfig?.isTransferPrice, isWarrantyPolicyOwner, effectiveConfig?.billedId, effectiveConfig?.lockChildrenPriceEditing, societyId]);

  const availablePermissions = useMemo(() => {
    const allPermissions = getPermissionsForUserType('EXTERNAL', primarySociety?.vendorType, societyOptions);
    const shouldHideSocietyPermissions = !hasChildren && (user?.additionalSocietyIds?.length || 0) === 0;
    if (shouldHideSocietyPermissions) {
      return allPermissions.filter(perm => perm.category !== 'society');
    }
    return allPermissions;
  }, [hasChildren, user?.additionalSocietyIds, primarySociety?.vendorType, societyOptions]);

  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [editingInfo, setEditingInfo] = useState(false);
  const [editingPermissions, setEditingPermissions] = useState(false);
  const [showConfirmInfo, setShowConfirmInfo] = useState(false);
  const [showConfirmPermissions, setShowConfirmPermissions] = useState(false);
  const [errorInfo, setErrorInfo] = useState<string | null>(null);
  const [errorPermissions, setErrorPermissions] = useState<string | null>(null);

  // Password change (own profile)
  const [editingPassword, setEditingPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorPassword, setErrorPassword] = useState<string | null>(null);
  const [passwordForm, setPasswordForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });

  // Admin password set
  const [editingAdminPassword, setEditingAdminPassword] = useState(false);
  const [showAdminPasswordConfirm, setShowAdminPasswordConfirm] = useState(false);
  const [showPasswordSuccess, setShowPasswordSuccess] = useState(false);
  const [adminPasswordForm, setAdminPasswordForm] = useState({ password: '', showPassword: false });
  const [errorAdminPassword, setErrorAdminPassword] = useState<string | null>(null);

  // Resend email
  const [showResendEmailConfirm, setShowResendEmailConfirm] = useState(false);

  const [formData, setFormData] = useState({
    civility: '', firstname: '', lastname: '', email: '', phone: '', position: '',
  });

  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);

  const generateRandomPassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789?!&%@-_';
    let password = '';
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    for (let i = 0; i < length; i++) {
      password += charset[array[i] % charset.length];
    }
    return password;
  };

  const isOwnProfile = useMemo(() => {
    return currentUser?.id === userId && currentUser?.type === 'EXTERNAL';
  }, [currentUser, userId]);

  const isAdminViewingExternal = useMemo(() => {
    return currentUser?.type === 'INTERNAL' && user && !isLoading;
  }, [currentUser, user, isLoading]);

  // Info changes diff
  const infoChanges = useMemo((): FieldChange[] => {
    if (!user) return [];
    const changes: FieldChange[] = [];
    (Object.keys(formData) as Array<keyof typeof formData>).forEach((key) => {
      const oldValue = user[key] || '';
      const newValue = formData[key] || '';
      if (oldValue !== newValue) {
        changes.push({ field: key, label: FIELD_LABELS[key] || key, oldValue: oldValue || '(vide)', newValue: newValue || '(vide)' });
      }
    });
    return changes;
  }, [formData, user]);

  // Permission changes diff
  const permissionsChanges = useMemo(() => {
    if (!user) return { added: [] as string[], removed: [] as string[] };
    const currentPerms = user.permissions || [];
    const added = selectedPermissions.filter(p => !currentPerms.includes(p));
    const removed = currentPerms.filter(p => !selectedPermissions.includes(p));
    return { added, removed };
  }, [selectedPermissions, user]);

  // ==================== Handlers ====================

  const handleEditInfo = () => {
    if (user) {
      setFormData({
        civility: user.civility || '', firstname: user.firstname || '', lastname: user.lastname || '',
        email: user.email || '', phone: user.phone || '', position: user.position || '',
      });
      setEditingInfo(true);
      setErrorInfo(null);
    }
  };

  const handleSubmitInfo = () => {
    if (infoChanges.length === 0) { setEditingInfo(false); return; }
    setShowConfirmInfo(true);
  };

  const handleConfirmInfo = async () => {
    try {
      setErrorInfo(null);
      await updateUser.mutateAsync({ id: userId, data: formData });
      setEditingInfo(false);
      setShowConfirmInfo(false);
      notify.success('Informations mises à jour avec succès');
    } catch (error: any) {
      setErrorInfo(error.response?.data?.message || 'Erreur lors de la mise à jour');
    }
  };

  const handleEditPermissions = () => {
    if (user) {
      const permissions = user.permissions?.includes('*')
        ? availablePermissions.map(p => p.code)
        : (user.permissions || []);
      setSelectedPermissions(permissions);
      setEditingPermissions(true);
      setErrorPermissions(null);
    }
  };

  const handleSubmitPermissions = () => {
    const hasChanges = permissionsChanges.added.length > 0 || permissionsChanges.removed.length > 0;
    if (!hasChanges) { setEditingPermissions(false); return; }
    setShowConfirmPermissions(true);
  };

  const handleConfirmPermissions = async () => {
    try {
      setErrorPermissions(null);
      await updatePermissions.mutateAsync({ id: userId, permissions: selectedPermissions });
      setEditingPermissions(false);
      setShowConfirmPermissions(false);
      notify.success('Permissions mises à jour avec succès');
    } catch (error: any) {
      setErrorPermissions(error.response?.data?.message || 'Erreur lors de la mise à jour');
    }
  };

  const handleEditPassword = () => {
    setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
    setEditingPassword(true);
    setErrorPassword(null);
  };

  const handleSubmitPassword = () => {
    setErrorPassword(null);
    if (!passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setErrorPassword('Tous les champs sont obligatoires'); return;
    }
    if (passwordForm.newPassword.length < 8) {
      setErrorPassword('Le nouveau mot de passe doit contenir au moins 8 caractères'); return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setErrorPassword('Les mots de passe ne correspondent pas'); return;
    }
    setShowConfirmPassword(true);
  };

  const handleConfirmPassword = async () => {
    try {
      setErrorPassword(null);
      await changePassword.mutateAsync({ oldPassword: passwordForm.oldPassword, newPassword: passwordForm.newPassword });
      setEditingPassword(false);
      setShowConfirmPassword(false);
      notify.success('Mot de passe modifié avec succès');
      if (onLogout) {
        setTimeout(() => onLogout(), 2000);
      }
    } catch (error: any) {
      setErrorPassword(error.response?.data?.message || 'Erreur lors du changement de mot de passe');
    }
  };

  const handleOpenSetPassword = () => {
    setAdminPasswordForm({ password: '', showPassword: false });
    setErrorAdminPassword(null);
    setEditingAdminPassword(true);
  };

  const handleGenerateRandomPassword = () => {
    const newPassword = generateRandomPassword();
    setAdminPasswordForm({ ...adminPasswordForm, password: newPassword });
  };

  const handleSubmitAdminPassword = () => {
    setErrorAdminPassword(null);
    if (!adminPasswordForm.password) { setErrorAdminPassword('Le mot de passe est obligatoire'); return; }
    if (adminPasswordForm.password.length < 8) { setErrorAdminPassword('Le mot de passe doit contenir au moins 8 caractères'); return; }
    setShowAdminPasswordConfirm(true);
  };

  const handleConfirmAdminPassword = async () => {
    try {
      setErrorAdminPassword(null);
      await setPassword.mutateAsync({ id: userId, password: adminPasswordForm.password });
      setShowAdminPasswordConfirm(false);
      setEditingAdminPassword(false);
      setShowPasswordSuccess(true);
    } catch (error: any) {
      setErrorAdminPassword(error.response?.data?.message || 'Erreur lors de la définition du mot de passe');
    }
  };

  const handleCopyPassword = () => {
    navigator.clipboard.writeText(adminPasswordForm.password);
    setShowPasswordSuccess(false);
    setAdminPasswordForm({ password: '', showPassword: false });
    notify.success('Mot de passe copié dans le presse-papier');
  };

  const handleResendEmailClick = () => { setShowResendEmailConfirm(true); };
  const handleCancelResendEmail = () => { setShowResendEmailConfirm(false); };

  const handleConfirmResendEmail = async () => {
    setShowResendEmailConfirm(false);
    try {
      await resendWelcomeEmail.mutateAsync(userId);
      notify.success('Email de bienvenue renvoyé avec succès');
    } catch (error: any) {
      notify.error(error.response?.data?.message || "Erreur lors de l'envoi de l'email");
    }
  };

  const handleImpersonate = async () => {
    if (!user || !onImpersonate) return;
    const confirmed = await confirmAction({
      title: 'Se connecter en tant que',
      message: `Êtes-vous sûr de vouloir vous connecter en tant que ${user.firstname} ${user.lastname} ? Vous verrez exactement ce que cet utilisateur voit.`,
      confirmLabel: 'Se connecter',
      variant: 'warning',
    });
    if (!confirmed) return;
    try {
      await onImpersonate(userId);
    } catch (error) {
      notify.error("Erreur lors de la connexion en tant que cet utilisateur");
    }
  };

  const handleDelete = async () => {
    if (!user) return;
    const confirmed = await confirmAction({
      title: "Supprimer l'utilisateur",
      message: `Attention : cette action est irréversible.\n\nL'utilisateur ${user.firstname} ${user.lastname} sera définitivement supprimé.`,
      confirmLabel: 'Supprimer',
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      await deleteUser.mutateAsync({ id: userId });
      notify.success('Utilisateur supprimé avec succès');
      onNavigate(basePath);
    } catch (error: any) {
      notify.error(error.response?.data?.message || "Erreur lors de la suppression");
    }
  };

  const categoryLabels: Record<string, string> = {
    dashboard: 'Tableau de bord',
    society: 'Sociétés',
    repairer: 'Réparateurs',
    internal_user: 'Utilisateurs internes',
    external_user: 'Utilisateurs externes',
    warranty: wording.plural,
    contract: 'Contrats',
    delivery_proof: 'Attestations',
    sav: 'SAV',
    invoice: 'Factures',
    monthly_payment: 'Paiements',
    signature: 'Signatures',
    stripe: 'Stripe',
  };

  const permissionsWithWording = useMemo(
    () => getAvailablePermissionsWithWording(primarySociety?.vendorType, societyOptions),
    [primarySociety?.vendorType, societyOptions]
  );

  const groupedAvailablePermissions = useMemo(() => {
    const groups: Record<string, typeof availablePermissions> = {};
    availablePermissions.forEach(perm => {
      if (!groups[perm.category]) groups[perm.category] = [];
      groups[perm.category].push(perm);
    });
    return groups;
  }, [availablePermissions]);

  const userPermissions = useMemo(() => {
    if (!user?.permissions) return new Set<string>();
    if (user.permissions.includes('*')) return new Set(availablePermissions.map(p => p.code));
    return new Set(user.permissions);
  }, [user?.permissions, availablePermissions]);

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) newExpanded.delete(category);
    else newExpanded.add(category);
    setExpandedCategories(newExpanded);
  };

  // ==================== Date formatters ====================

  const formatDateFr = (date: Date | string | null | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const formatDateTimeFr = (date: Date | string | null | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  // ==================== Loading / Error ====================

  if (isLoading) {
    return (
      <div className="tw-min-h-screen tw-bg-gray-50 tw-flex tw-items-center tw-justify-center">
        <Loader2 className="tw-w-8 tw-h-8 tw-animate-spin tw-text-affinicar-primary" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="tw-min-h-screen tw-bg-gray-50 tw-p-8">
        <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-4">
          <p className="tw-text-red-800">Erreur lors du chargement de l'utilisateur</p>
        </div>
      </div>
    );
  }

  // ==================== Render ====================

  return (
    <div className={embedded ? (className || '') : `tw-min-h-screen tw-bg-gray-50 ${className || ''}`}>
      {/* Header */}
      <div className={embedded ? undefined : 'tw-px-6'}>
        <DetailHeader
          type="user"
          title={`${user.firstname} ${user.lastname}`}
          status={user.isActive ? { label: 'Actif', variant: 'active' } : { label: 'Inactif', variant: 'expired' }}
          breadcrumb={[
            embedded ? { label: 'Utilisateurs', onClick: () => onNavigate(basePath) } : { label: 'Utilisateurs', href: basePath },
            { label: `${user.firstname} ${user.lastname}` },
          ]}
          metadata={[
            { label: 'Rôle', value: user.roleType === 'ADMIN' ? 'Administrateur' : 'Utilisateur' },
            { label: 'Création', value: formatDateFr(user.createdAt) },
            { label: 'Dernière connexion', value: formatDateTimeFr(user.lastLoginAt) },
          ]}
          kebabMenuItems={[
            ...(!isOwnProfile && perms.canResendWelcomeEmail ? [{
              label: 'Envoyer un mot de passe temporaire',
              icon: <Send className="tw-w-4 tw-h-4" />,
              onClick: handleResendEmailClick,
            }] : []),
            ...(isAdminViewingExternal && perms.canEditPassword ? [{
              label: 'Générer un mot de passe',
              icon: <Key className="tw-w-4 tw-h-4" />,
              onClick: handleOpenSetPassword,
            }] : []),
            ...(isAdminViewingExternal && perms.canImpersonate && onImpersonate ? [{
              label: 'Se connecter en tant que',
              icon: <UserCheck className="tw-w-4 tw-h-4" />,
              onClick: () => { handleImpersonate(); },
            }] : []),
            ...(!isOwnProfile && perms.canDelete ? [{
              label: 'Supprimer',
              icon: <Trash2 className="tw-w-4 tw-h-4" />,
              onClick: () => { handleDelete(); },
              variant: 'danger' as const,
            }] : []),
          ]}
        />
      </div>

      {/* Main content */}
      <div className={embedded ? 'tw-py-6 tw-space-y-4' : 'tw-px-6 tw-py-6 tw-space-y-4'}>

        {/* Password block (own profile) */}
        {isOwnProfile && (
          <div className="tw-bg-white tw-rounded-md tw-border tw-border-gray-200 tw-shadow-sm tw-px-4 tw-py-3">
            <div className="tw-flex tw-items-center tw-justify-between">
              <div className="tw-flex tw-items-center tw-gap-3">
                <Lock className="tw-w-4 tw-h-4 tw-text-gray-400" />
                <div>
                  <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-tracking-wide">Mot de passe</span>
                  <p className="tw-text-sm tw-text-gray-600 tw-mb-0">••••••••••••</p>
                </div>
              </div>
              {perms.canEditPassword && (
                <button
                  onClick={handleEditPassword}
                  className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-text-sm tw-text-affinicar-primary hover:tw-text-affinicar-secondary hover:tw-bg-affinicar-lighter tw-rounded-md tw-transition-colors"
                >
                  <Edit className="tw-w-4 tw-h-4" />
                  Modifier
                </button>
              )}
            </div>
          </div>
        )}

        {/* 3-column grid */}
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">

          {/* Personal info */}
          <InfoCard title="Informations" icon={<User className="tw-w-4 tw-h-4" />} editable={perms.canEditPersonalInfo} onEdit={handleEditInfo}>
            <div className="tw-space-y-0.5">
              <InfoRow label="Nom" value={`${user.civility ? `${user.civility} ` : ''}${user.firstname} ${user.lastname}`} icon={<User className="tw-w-3.5 tw-h-3.5" />} />
              <InfoRow label="Email" value={user.email || '—'} icon={<Mail className="tw-w-3.5 tw-h-3.5" />} href={user.email ? `mailto:${user.email}` : undefined} />
              <InfoRow label="Téléphone" value={user.phone || '—'} icon={<Phone className="tw-w-3.5 tw-h-3.5" />} href={user.phone ? `tel:${user.phone}` : undefined} />
              <InfoRow label="Poste" value={user.position || '—'} icon={<Briefcase className="tw-w-3.5 tw-h-3.5" />} />
            </div>
          </InfoCard>

          {/* Societies */}
          <SocietiesAssignmentCard
            userId={userId}
            notify={notify}
            canEditSocieties={perms.canEditSocieties}
          />

          {/* Permissions */}
          <div className="tw-bg-white tw-rounded-md tw-border tw-border-gray-200 tw-p-4 tw-shadow-sm">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-2.5">
              <div className="tw-flex tw-items-center tw-gap-2">
                <Shield className="tw-w-5 tw-h-5 tw-text-affinicar-primary" />
                <h3 className="tw-text-base tw-font-semibold tw-text-gray-900 tw-mb-0">Permissions</h3>
              </div>
              {perms.canEditPermissions && (
                <button
                  onClick={handleEditPermissions}
                  className="tw-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-text-xs tw-text-affinicar-primary hover:tw-text-affinicar-secondary hover:tw-bg-affinicar-lighter tw-rounded tw-transition-colors"
                >
                  <Edit className="tw-w-3 tw-h-3" />
                  Modifier
                </button>
              )}
            </div>

            <div className="tw-space-y-1 tw-max-h-[400px] tw-overflow-y-auto">
              {Object.entries(groupedAvailablePermissions).map(([category, permissions]) => {
                const isExpanded = expandedCategories.has(category);
                const ownedCount = permissions.filter(p => userPermissions.has(p.code)).length;

                return (
                  <div key={category} className="tw-border tw-border-gray-200 tw-rounded tw-overflow-hidden">
                    <button
                      type="button"
                      onClick={() => toggleCategory(category)}
                      className="tw-w-full tw-flex tw-items-center tw-justify-between tw-px-2.5 tw-py-1.5 tw-bg-gray-50 hover:tw-bg-gray-100 tw-transition-colors"
                    >
                      <div className="tw-flex tw-items-center tw-gap-1.5">
                        <span className="tw-text-xs tw-text-gray-500">{isExpanded ? '▼' : '▶'}</span>
                        <span className="tw-text-xs tw-font-medium tw-text-gray-900">
                          {categoryLabels[category] || category}
                        </span>
                      </div>
                      <span className={`tw-text-xs tw-font-medium ${ownedCount === permissions.length ? 'tw-text-green-600' : ownedCount > 0 ? 'tw-text-gray-500' : 'tw-text-gray-400'}`}>
                        {ownedCount}/{permissions.length}
                      </span>
                    </button>

                    {isExpanded && (
                      <div className="tw-px-2.5 tw-py-1.5 tw-space-y-1 tw-bg-white">
                        {permissions.map(perm => {
                          const hasPermission = userPermissions.has(perm.code);
                          return (
                            <div key={perm.code} className={`tw-flex tw-items-start tw-gap-1.5 tw-px-2 tw-py-1 tw-rounded ${hasPermission ? 'tw-bg-green-50' : 'tw-bg-gray-50'}`}>
                              <span className={`tw-mt-0.5 tw-text-xs ${hasPermission ? 'tw-text-green-600' : 'tw-text-gray-300'}`}>
                                {hasPermission ? '✓' : '✗'}
                              </span>
                              <div className="tw-flex-1">
                                <div className={`tw-text-xs tw-font-medium ${hasPermission ? 'tw-text-gray-900' : 'tw-text-gray-400'}`}>{perm.label}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Audit logs */}
      {perms.canViewAuditLogs && (
        <div className={embedded ? 'tw-mt-2' : 'tw-px-6 tw-mt-2'}>
          <GenericAuditLogsCard entityType="external-user" entityId={userId} />
        </div>
      )}

      {/* ==================== MODALS ==================== */}

      {/* Edit personal info modal */}
      {editingInfo && (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-2xl tw-w-full tw-max-h-[90vh] tw-overflow-y-auto tw-m-4">
            <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b">
              <h3 className="tw-text-xl tw-font-semibold tw-text-gray-900">Modifier les informations</h3>
              <button onClick={() => setEditingInfo(false)} className="tw-text-gray-400 hover:tw-text-gray-600">
                <X className="tw-w-5 tw-h-5" />
              </button>
            </div>

            <div className="tw-p-6 tw-space-y-4">
              <div>
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">Civilité</label>
                <select
                  value={formData.civility}
                  onChange={(e) => setFormData({ ...formData, civility: e.target.value })}
                  className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary"
                >
                  <option value="">Sélectionner</option>
                  <option value="M.">M.</option>
                  <option value="Mme">Mme</option>
                </select>
              </div>

              <div>
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">Prénom *</label>
                <input
                  type="text"
                  value={formData.firstname}
                  onChange={(e) => setFormData({ ...formData, firstname: e.target.value })}
                  className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary"
                  required
                />
              </div>

              <div>
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">Nom *</label>
                <input
                  type="text"
                  value={formData.lastname}
                  onChange={(e) => setFormData({ ...formData, lastname: e.target.value })}
                  className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary"
                  required
                />
              </div>

              <div>
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary"
                  required
                />
              </div>

              <div>
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">Téléphone</label>
                <PhoneInput value={formData.phone} onChange={(value) => setFormData({ ...formData, phone: value })} label="" />
              </div>

              <div>
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">Poste</label>
                <input
                  type="text"
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary"
                />
              </div>
            </div>

            <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-p-6 tw-border-t">
              <button onClick={() => setEditingInfo(false)} className="tw-px-4 tw-py-2 tw-text-gray-700 tw-bg-gray-100 tw-rounded-md hover:tw-bg-gray-200 tw-transition-colors">
                Annuler
              </button>
              <button onClick={handleSubmitInfo} className="tw-px-4 tw-py-2 tw-bg-affinicar-primary tw-text-white tw-rounded-md hover:tw-bg-affinicar-secondary tw-transition-colors">
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit permissions modal */}
      {editingPermissions && (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-4xl tw-w-full tw-max-h-[90vh] tw-overflow-y-auto tw-m-4">
            <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b">
              <h3 className="tw-text-xl tw-font-semibold tw-text-gray-900">Modifier les permissions</h3>
              <button onClick={() => setEditingPermissions(false)} className="tw-text-gray-400 hover:tw-text-gray-600">
                <X className="tw-w-5 tw-h-5" />
              </button>
            </div>

            <div className="tw-p-6">
              <PermissionsSelector
                availablePermissions={availablePermissions}
                selectedPermissions={selectedPermissions}
                onChange={setSelectedPermissions}
              />
            </div>

            <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-p-6 tw-border-t">
              <button onClick={() => setEditingPermissions(false)} className="tw-px-4 tw-py-2 tw-text-gray-700 tw-bg-gray-100 tw-rounded-md hover:tw-bg-gray-200 tw-transition-colors">
                Annuler
              </button>
              <button onClick={handleSubmitPermissions} className="tw-px-4 tw-py-2 tw-bg-affinicar-primary tw-text-white tw-rounded-md hover:tw-bg-affinicar-secondary tw-transition-colors">
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm info changes modal */}
      {showConfirmInfo && (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-[101] tw-p-4">
          <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-2xl tw-w-full tw-max-h-[80vh] tw-overflow-y-auto">
            <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b tw-border-gray-200">
              <div className="tw-flex tw-items-center tw-gap-3">
                <div className="tw-w-10 tw-h-10 tw-bg-affinicar-lighter tw-rounded-full tw-flex tw-items-center tw-justify-center">
                  <CheckCircle className="tw-w-6 tw-h-6 tw-text-affinicar-primary" />
                </div>
                <h2 className="tw-text-xl tw-font-bold tw-text-gray-900">Confirmer les modifications</h2>
              </div>
              <button onClick={() => setShowConfirmInfo(false)} disabled={updateUser.isLoading} className="tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors disabled:tw-opacity-50">
                <X className="tw-w-5 tw-h-5" />
              </button>
            </div>

            <div className="tw-p-6">
              <p className="tw-text-sm tw-text-gray-600 tw-mb-4">Vous êtes sur le point de modifier les informations suivantes :</p>
              <div className="tw-space-y-3">
                {infoChanges.map((change, index) => (
                  <div key={index} className="tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-p-4">
                    <div className="tw-flex tw-items-start tw-gap-3">
                      <div className="tw-flex-1">
                        <p className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-2">{change.label}</p>
                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
                          <div>
                            <p className="tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">Ancienne valeur :</p>
                            <p className="tw-text-sm tw-text-gray-700 tw-bg-white tw-p-2 tw-rounded tw-border tw-border-gray-200">{change.oldValue}</p>
                          </div>
                          <div>
                            <p className="tw-text-xs tw-font-medium tw-text-green-600 tw-mb-1">Nouvelle valeur :</p>
                            <p className="tw-text-sm tw-text-green-700 tw-bg-green-50 tw-p-2 tw-rounded tw-border tw-border-green-200 tw-font-medium">{change.newValue}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {errorInfo && (
                <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-4 tw-mt-4">
                  <div className="tw-flex tw-items-start tw-gap-3">
                    <AlertCircle className="tw-w-5 tw-h-5 tw-text-red-600 tw-flex-shrink-0 tw-mt-0.5" />
                    <p className="tw-text-sm tw-text-red-800">{errorInfo}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="tw-flex tw-justify-end tw-gap-3 tw-p-6 tw-border-t tw-border-gray-200">
              <button type="button" onClick={() => setShowConfirmInfo(false)} disabled={updateUser.isLoading} className="tw-px-6 tw-py-2.5 tw-bg-gray-200 tw-text-gray-700 tw-font-medium tw-rounded-lg hover:tw-bg-gray-300 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed">
                Retour
              </button>
              <button type="button" onClick={handleConfirmInfo} disabled={updateUser.isLoading} className="tw-flex tw-items-center tw-gap-2 tw-px-6 tw-py-2.5 tw-bg-affinicar-primary tw-text-white tw-font-medium tw-rounded-lg hover:tw-bg-affinicar-secondary tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed">
                {updateUser.isLoading ? (<><Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />Enregistrement...</>) : 'Confirmer et enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Own password change modal */}
      {editingPassword && (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-md tw-w-full tw-m-4">
            <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b">
              <h3 className="tw-text-xl tw-font-semibold tw-text-gray-900">Modifier mon mot de passe</h3>
              <button onClick={() => setEditingPassword(false)} className="tw-text-gray-400 hover:tw-text-gray-600">
                <X className="tw-w-5 tw-h-5" />
              </button>
            </div>

            <div className="tw-p-6 tw-space-y-4">
              <div>
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">Ancien mot de passe *</label>
                <input type="password" value={passwordForm.oldPassword} onChange={(e) => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })} className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary" required />
              </div>
              <div>
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">Nouveau mot de passe *</label>
                <input type="password" value={passwordForm.newPassword} onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })} className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary" required />
                <p className="tw-text-xs tw-text-gray-500 tw-mt-1">Au moins 8 caractères</p>
              </div>
              <div>
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">Confirmer le mot de passe *</label>
                <input type="password" value={passwordForm.confirmPassword} onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })} className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary" required />
              </div>
              {errorPassword && (
                <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-3">
                  <p className="tw-text-sm tw-text-red-800">{errorPassword}</p>
                </div>
              )}
            </div>

            <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-p-6 tw-border-t">
              <button onClick={() => setEditingPassword(false)} className="tw-px-4 tw-py-2 tw-text-gray-700 tw-bg-gray-100 tw-rounded-md hover:tw-bg-gray-200 tw-transition-colors">Annuler</button>
              <button onClick={handleSubmitPassword} className="tw-px-4 tw-py-2 tw-bg-affinicar-primary tw-text-white tw-rounded-md hover:tw-bg-affinicar-secondary tw-transition-colors">Modifier</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm own password change modal */}
      {showConfirmPassword && (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-[101] tw-p-4">
          <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-md tw-w-full">
            <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b tw-border-gray-200">
              <div className="tw-flex tw-items-center tw-gap-3">
                <div className="tw-w-10 tw-h-10 tw-bg-affinicar-lighter tw-rounded-full tw-flex tw-items-center tw-justify-center">
                  <CheckCircle className="tw-w-6 tw-h-6 tw-text-affinicar-primary" />
                </div>
                <h2 className="tw-text-xl tw-font-bold tw-text-gray-900">Confirmer le changement</h2>
              </div>
              <button onClick={() => setShowConfirmPassword(false)} disabled={changePassword.isLoading} className="tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors disabled:tw-opacity-50">
                <X className="tw-w-5 tw-h-5" />
              </button>
            </div>
            <div className="tw-p-6">
              <p className="tw-text-sm tw-text-gray-600">Êtes-vous sûr de vouloir modifier votre mot de passe ? Vous serez déconnecté et devrez vous reconnecter avec votre nouveau mot de passe.</p>
              {errorPassword && (
                <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-4 tw-mt-4">
                  <div className="tw-flex tw-items-start tw-gap-3">
                    <AlertCircle className="tw-w-5 tw-h-5 tw-text-red-600 tw-flex-shrink-0 tw-mt-0.5" />
                    <p className="tw-text-sm tw-text-red-800">{errorPassword}</p>
                  </div>
                </div>
              )}
            </div>
            <div className="tw-flex tw-justify-end tw-gap-3 tw-p-6 tw-border-t tw-border-gray-200">
              <button type="button" onClick={() => setShowConfirmPassword(false)} disabled={changePassword.isLoading} className="tw-px-6 tw-py-2.5 tw-bg-gray-200 tw-text-gray-700 tw-font-medium tw-rounded-lg hover:tw-bg-gray-300 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed">Annuler</button>
              <button type="button" onClick={handleConfirmPassword} disabled={changePassword.isLoading} className="tw-flex tw-items-center tw-gap-2 tw-px-6 tw-py-2.5 tw-bg-affinicar-primary tw-text-white tw-font-medium tw-rounded-lg hover:tw-bg-affinicar-secondary tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed">
                {changePassword.isLoading ? (<><Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />Modification...</>) : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin set password modal */}
      {editingAdminPassword && (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-md tw-w-full tw-m-4">
            <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b">
              <h3 className="tw-text-xl tw-font-semibold tw-text-gray-900">Définir un mot de passe</h3>
              <button onClick={() => setEditingAdminPassword(false)} className="tw-text-gray-400 hover:tw-text-gray-600">
                <X className="tw-w-5 tw-h-5" />
              </button>
            </div>

            <div className="tw-p-6 tw-space-y-4">
              <div>
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">Mot de passe *</label>
                <div className="tw-relative">
                  <input
                    type={adminPasswordForm.showPassword ? "text" : "password"}
                    value={adminPasswordForm.password}
                    onChange={(e) => setAdminPasswordForm({ ...adminPasswordForm, password: e.target.value })}
                    className="tw-w-full tw-px-3 tw-py-2 tw-pr-24 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary"
                    placeholder="Tapez ou générez un mot de passe"
                    required
                  />
                  <div className="tw-absolute tw-right-1 tw-top-1 tw-flex tw-gap-1">
                    <button type="button" onClick={handleGenerateRandomPassword} className="tw-p-1.5 tw-text-affinicar-primary hover:tw-bg-affinicar-lighter tw-rounded tw-transition-colors" title="Générer un mot de passe aléatoire">
                      <RefreshCw className="tw-w-4 tw-h-4" />
                    </button>
                    <button type="button" onClick={() => setAdminPasswordForm({ ...adminPasswordForm, showPassword: !adminPasswordForm.showPassword })} className="tw-p-1.5 tw-text-gray-600 hover:tw-bg-gray-50 tw-rounded tw-transition-colors" title={adminPasswordForm.showPassword ? "Masquer" : "Afficher"}>
                      {adminPasswordForm.showPassword ? <EyeOff className="tw-w-4 tw-h-4" /> : <Eye className="tw-w-4 tw-h-4" />}
                    </button>
                  </div>
                </div>
                <p className="tw-text-xs tw-text-gray-500 tw-mt-1">Au moins 8 caractères</p>
              </div>
              {errorAdminPassword && (
                <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-3">
                  <p className="tw-text-sm tw-text-red-800">{errorAdminPassword}</p>
                </div>
              )}
            </div>

            <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-p-6 tw-border-t">
              <button onClick={() => setEditingAdminPassword(false)} className="tw-px-4 tw-py-2 tw-text-gray-700 tw-bg-gray-100 tw-rounded-md hover:tw-bg-gray-200 tw-transition-colors">Annuler</button>
              <button onClick={handleSubmitAdminPassword} className="tw-px-4 tw-py-2 tw-bg-affinicar-primary tw-text-white tw-rounded-md hover:tw-bg-affinicar-secondary tw-transition-colors">Appliquer</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm admin password modal */}
      {showAdminPasswordConfirm && (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-[101] tw-p-4">
          <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-md tw-w-full">
            <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b tw-border-gray-200">
              <div className="tw-flex tw-items-center tw-gap-3">
                <div className="tw-w-10 tw-h-10 tw-bg-affinicar-lighter tw-rounded-full tw-flex tw-items-center tw-justify-center">
                  <CheckCircle className="tw-w-6 tw-h-6 tw-text-affinicar-primary" />
                </div>
                <h2 className="tw-text-xl tw-font-bold tw-text-gray-900">Confirmer le mot de passe</h2>
              </div>
              <button onClick={() => setShowAdminPasswordConfirm(false)} disabled={setPassword.isLoading} className="tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors disabled:tw-opacity-50">
                <X className="tw-w-5 tw-h-5" />
              </button>
            </div>
            <div className="tw-p-6">
              <p className="tw-text-sm tw-text-gray-600 tw-mb-4">Êtes-vous sûr de vouloir définir ce mot de passe pour cet utilisateur ? L'ancien mot de passe ne sera plus valide.</p>
              <div className="tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-p-3">
                <p className="tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">Mot de passe :</p>
                <code className="tw-text-sm tw-font-mono tw-text-gray-900">
                  {'•'.repeat(adminPasswordForm.password.length)} ({adminPasswordForm.password.length} caractères)
                </code>
              </div>
              {errorAdminPassword && (
                <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-4 tw-mt-4">
                  <div className="tw-flex tw-items-start tw-gap-3">
                    <AlertCircle className="tw-w-5 tw-h-5 tw-text-red-600 tw-flex-shrink-0 tw-mt-0.5" />
                    <p className="tw-text-sm tw-text-red-800">{errorAdminPassword}</p>
                  </div>
                </div>
              )}
            </div>
            <div className="tw-flex tw-justify-end tw-gap-3 tw-p-6 tw-border-t tw-border-gray-200">
              <button type="button" onClick={() => setShowAdminPasswordConfirm(false)} disabled={setPassword.isLoading} className="tw-px-6 tw-py-2.5 tw-bg-gray-200 tw-text-gray-700 tw-font-medium tw-rounded-lg hover:tw-bg-gray-300 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed">Retour</button>
              <button type="button" onClick={handleConfirmAdminPassword} disabled={setPassword.isLoading} className="tw-flex tw-items-center tw-gap-2 tw-px-6 tw-py-2.5 tw-bg-affinicar-primary tw-text-white tw-font-medium tw-rounded-lg hover:tw-bg-affinicar-secondary tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed">
                {setPassword.isLoading ? (<><Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />Application...</>) : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password success modal */}
      {showPasswordSuccess && (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-[101] tw-p-4">
          <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-md tw-w-full">
            <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b tw-border-gray-200">
              <div className="tw-flex tw-items-center tw-gap-3">
                <div className="tw-w-10 tw-h-10 tw-bg-green-100 tw-rounded-full tw-flex tw-items-center tw-justify-center">
                  <CheckCircle className="tw-w-6 tw-h-6 tw-text-green-600" />
                </div>
                <h2 className="tw-text-xl tw-font-bold tw-text-gray-900">Mot de passe défini</h2>
              </div>
            </div>
            <div className="tw-p-6">
              <p className="tw-text-sm tw-text-gray-600 tw-mb-4">Le mot de passe a été défini avec succès. Veuillez le copier et le transmettre à l'utilisateur de manière sécurisée.</p>
              <div className="tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-p-4">
                <p className="tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-2">Mot de passe :</p>
                <div className="tw-flex tw-items-center tw-gap-2">
                  <code className="tw-flex-1 tw-text-base tw-font-mono tw-text-gray-900 tw-bg-white tw-px-3 tw-py-2 tw-rounded tw-border tw-border-gray-300 tw-break-all">
                    {adminPasswordForm.password}
                  </code>
                  <button onClick={handleCopyPassword} className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-bg-affinicar-primary tw-text-white tw-rounded-md hover:tw-bg-affinicar-secondary tw-transition-colors tw-flex-shrink-0" title="Copier et fermer">
                    <Copy className="tw-w-4 tw-h-4" />
                  </button>
                </div>
              </div>
              <div className="tw-bg-yellow-50 tw-border tw-border-yellow-200 tw-rounded-lg tw-p-3 tw-mt-4">
                <p className="tw-text-xs tw-text-yellow-800">
                  <strong>Important :</strong> Ce mot de passe ne sera affiché qu'une seule fois. Assurez-vous de le copier avant de fermer cette fenêtre.
                </p>
              </div>
            </div>
            <div className="tw-flex tw-justify-end tw-gap-3 tw-p-6 tw-border-t tw-border-gray-200">
              <button type="button" onClick={() => { setShowPasswordSuccess(false); setAdminPasswordForm({ password: '', showPassword: false }); }} className="tw-px-6 tw-py-2.5 tw-bg-affinicar-primary tw-text-white tw-font-medium tw-rounded-lg hover:tw-bg-affinicar-secondary tw-transition-colors">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm permissions modal */}
      {showConfirmPermissions && (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-[101] tw-p-4">
          <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-2xl tw-w-full tw-max-h-[80vh] tw-overflow-y-auto">
            <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b tw-border-gray-200">
              <div className="tw-flex tw-items-center tw-gap-3">
                <div className="tw-w-10 tw-h-10 tw-bg-affinicar-lighter tw-rounded-full tw-flex tw-items-center tw-justify-center">
                  <CheckCircle className="tw-w-6 tw-h-6 tw-text-affinicar-primary" />
                </div>
                <h2 className="tw-text-xl tw-font-bold tw-text-gray-900">Confirmer les modifications</h2>
              </div>
              <button onClick={() => setShowConfirmPermissions(false)} disabled={updatePermissions.isLoading} className="tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors disabled:tw-opacity-50">
                <X className="tw-w-5 tw-h-5" />
              </button>
            </div>
            <div className="tw-p-6">
              <p className="tw-text-sm tw-text-gray-600 tw-mb-4">Récapitulatif des changements de permissions :</p>
              {permissionsChanges.added.length > 0 && (
                <div className="tw-mb-4">
                  <h3 className="tw-text-sm tw-font-semibold tw-text-green-700 tw-mb-2 tw-flex tw-items-center tw-gap-2">
                    <span className="tw-w-6 tw-h-6 tw-bg-green-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-xs">+{permissionsChanges.added.length}</span>
                    Permissions ajoutées
                  </h3>
                  <div className="tw-space-y-2">
                    {permissionsChanges.added.map(code => {
                      const perm = permissionsWithWording.find(p => p.code === code);
                      return (
                        <div key={code} className="tw-bg-green-50 tw-border tw-border-green-200 tw-rounded-lg tw-p-3">
                          <p className="tw-text-sm tw-font-medium tw-text-green-900">{perm?.label || code}</p>
                          {perm?.description && <p className="tw-text-xs tw-text-green-700 tw-mt-1">{perm.description}</p>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {permissionsChanges.removed.length > 0 && (
                <div className="tw-mb-4">
                  <h3 className="tw-text-sm tw-font-semibold tw-text-red-700 tw-mb-2 tw-flex tw-items-center tw-gap-2">
                    <span className="tw-w-6 tw-h-6 tw-bg-red-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-xs">-{permissionsChanges.removed.length}</span>
                    Permissions retirées
                  </h3>
                  <div className="tw-space-y-2">
                    {permissionsChanges.removed.map(code => {
                      const perm = permissionsWithWording.find(p => p.code === code);
                      return (
                        <div key={code} className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-3">
                          <p className="tw-text-sm tw-font-medium tw-text-red-900">{perm?.label || code}</p>
                          {perm?.description && <p className="tw-text-xs tw-text-red-700 tw-mt-1">{perm.description}</p>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {errorPermissions && (
                <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-4 tw-mt-4">
                  <div className="tw-flex tw-items-start tw-gap-3">
                    <AlertCircle className="tw-w-5 tw-h-5 tw-text-red-600 tw-flex-shrink-0 tw-mt-0.5" />
                    <p className="tw-text-sm tw-text-red-800">{errorPermissions}</p>
                  </div>
                </div>
              )}
            </div>
            <div className="tw-flex tw-justify-end tw-gap-3 tw-p-6 tw-border-t tw-border-gray-200">
              <button type="button" onClick={() => setShowConfirmPermissions(false)} disabled={updatePermissions.isLoading} className="tw-px-6 tw-py-2.5 tw-bg-gray-200 tw-text-gray-700 tw-font-medium tw-rounded-lg hover:tw-bg-gray-300 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed">Retour</button>
              <button type="button" onClick={handleConfirmPermissions} disabled={updatePermissions.isLoading} className="tw-flex tw-items-center tw-gap-2 tw-px-6 tw-py-2.5 tw-bg-affinicar-primary tw-text-white tw-font-medium tw-rounded-lg hover:tw-bg-affinicar-secondary tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed">
                {updatePermissions.isLoading ? (<><Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />Enregistrement...</>) : 'Confirmer et enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resend welcome email confirmation modal */}
      {showResendEmailConfirm && (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black tw-bg-opacity-50">
          <div className="tw-bg-white tw-rounded-xl tw-shadow-2xl tw-max-w-md tw-w-full tw-m-4">
            <div className="tw-p-6 tw-border-b tw-border-gray-200">
              <div className="tw-flex tw-items-center tw-justify-between">
                <h3 className="tw-text-xl tw-font-semibold tw-text-gray-900">Renvoyer l'email de bienvenue</h3>
                <button onClick={handleCancelResendEmail} className="tw-text-gray-400 hover:tw-text-gray-600">
                  <X className="tw-w-5 tw-h-5" />
                </button>
              </div>
            </div>
            <div className="tw-p-6">
              <div className="tw-flex tw-items-start tw-gap-3 tw-mb-4">
                <AlertCircle className="tw-w-6 tw-h-6 tw-text-yellow-600 tw-flex-shrink-0" />
                <div>
                  <p className="tw-text-sm tw-text-gray-700 tw-mb-2">
                    Un nouveau mot de passe temporaire sera généré et envoyé par email à <strong>{user?.email}</strong>.
                  </p>
                  <p className="tw-text-sm tw-text-gray-700">
                    L'utilisateur devra obligatoirement changer ce mot de passe lors de sa prochaine connexion.
                  </p>
                </div>
              </div>
            </div>
            <div className="tw-flex tw-justify-end tw-gap-3 tw-p-6 tw-border-t tw-border-gray-200">
              <button onClick={handleCancelResendEmail} className="tw-px-6 tw-py-2.5 tw-bg-gray-200 tw-text-gray-700 tw-font-medium tw-rounded-lg hover:tw-bg-gray-300 tw-transition-colors">Annuler</button>
              <button onClick={handleConfirmResendEmail} className="tw-flex tw-items-center tw-gap-2 tw-px-6 tw-py-2.5 tw-bg-affinicar-primary tw-text-white tw-font-medium tw-rounded-lg hover:tw-bg-affinicar-secondary tw-transition-colors">
                <Send className="tw-w-4 tw-h-4" />
                Envoyer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
